export const CONSTRAINT_KEYS = ['minHourly', 'maxHourly', 'minDaily', 'maxDaily', 'minMonthly', 'maxMonthly', 'minYearly', 'maxYearly'] as const;
export type ConstraintKey = (typeof CONSTRAINT_KEYS)[number];

export interface VariablePrice {
    /** `YYYY-MM` (mensuel) or `YYYY-MM-DD HH` (horaire). */
    date: string;
    price: number | null;
}

export interface FuelContract extends CreationMeta {
    id: string;
    name: string;
    linkedUnits: number;
    fixedPrice: number | null;
    /** J/u */
    calorificValue: number;
    description: string;
    variablePrices: VariablePrice[];
    constraints: Record<ConstraintKey, number | null>;
    /** Nemo `combustible_start_hour/day/month`: start of the annual constraints (all or none). */
    startHour?: number | null;
    startDay?: number | null;
    startMonth?: number | null;
}

/** Nemo `CreationInfo` metadata (ISO dates), shown at the bottom of edit panels. */
export interface CreationMeta {
    createdBy?: { firstname: string; lastname: string } | null;
    creationDate?: string | null;
    modificationDate?: string | null;
}

export interface EnrRate extends CreationMeta {
    id: string;
    name: string;
    /** Contribution (%) of each fuel contract, keyed by contract id. Missing = 0 %. */
    shares: Record<string, number>;
    min: number | null;
    max: number | null;
    /** Nemo advanced options: period on which the thresholds apply (day / month). */
    startDay: number;
    startMonth: number;
    endDay: number;
    endMonth: number;
}

export interface Pollutant extends CreationMeta {
    id: string;
    name: string;
    /** Emission factor (kg/J) per fuel contract id. */
    emissions: Record<string, number | null>;
    fixedPrice: number | null;
    /** Nemo « Prix variable (mensuel) » rows (`YYYY-MM`, €/kg). */
    variablePrices: VariablePrice[];
    /** Nemo bound (advanced options): max threshold (kg) on a `JJ-MM` → `JJ-MM` period. */
    maxThreshold: number | null;
    boundStart: string | null;
    boundEnd: string | null;
}

export interface FuelsData {
    contracts: FuelContract[];
    enrRates: EnrRate[];
    pollutants: Pollutant[];
}

export type FuelsCollection = keyof FuelsData;

export type FuelContractInput = Pick<FuelContract, 'name' | 'calorificValue' | 'fixedPrice' | 'description' | 'variablePrices'>;
export type EnrRateInput = Omit<EnrRate, 'id' | keyof CreationMeta>;
export type PollutantInput = Omit<Pollutant, 'id' | keyof CreationMeta>;
